import React, { useState, useCallback, useMemo } from 'react';
import { theme } from '../../theme';
import { useLanguage } from '../../i18n/useLanguage';
import { useWorkplaceSovData } from '../../hooks/useWorkplaceSovData';
import { useWorkplaceSovState } from '../../hooks/useWorkplaceSovState';
import { calculateOverallSovScore, infrastructureToDeployment } from '../../utils/sovScoreCalculations';
import { Header } from '../Header';
import { SidebarMenuItem } from '../Sidebar';
import { EmailModal } from '../EmailModal';
import { WorkplaceProgressBar } from './WorkplaceProgressBar';
import { InfrastructureStep } from './InfrastructureStep';
import { IstZustandStep } from './IstZustandStep';
import { IstScoreStep } from './IstScoreStep';
import { SovWeightStep } from './SovWeightStep';
import { SolutionBuilder } from './SolutionBuilder';
import { WorkplaceResults } from './WorkplaceResults';
import { generateWorkplacePdfBase64 } from '../../services/pdfExport';
import { sendReport } from '../../services/apiClient';
import type { DeploymentModel, WorkplaceSovStep } from '../../types/workplace';

interface WorkplaceComparisonProps {
  onBack: () => void;
  onOpenRegulatory: () => void;
  onSwitchToCloud?: () => void;
  onOpenDatenschutz?: () => void;
}

export const WorkplaceComparison: React.FC<WorkplaceComparisonProps> = ({ onBack, onOpenRegulatory, onSwitchToCloud, onOpenDatenschutz }) => {
  const { t, language } = useLanguage();
  const { sovData, levels, aggregatedProviders, sealDefinitions, error } = useWorkplaceSovData();
  const state = useWorkplaceSovState();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  // 1. Derived values
  const allSubPoints = useMemo(
    () => levels.flatMap(l => l.subPoints),
    [levels],
  );

  const subPointIds = useMemo(
    () => allSubPoints.map(sp => sp.id),
    [allSubPoints],
  );

  const globalDeployment = useMemo<DeploymentModel | null>(
    () => (state.infrastructureMode ? infrastructureToDeployment(state.infrastructureMode) : null),
    [state.infrastructureMode],
  );

  const istScore = useMemo(
    () => calculateOverallSovScore(state.istSelections, state.sovWeights, sovData, subPointIds, state.istOperationsAnswers, allSubPoints),
    [state.istSelections, state.sovWeights, sovData, subPointIds, state.istOperationsAnswers, allSubPoints],
  );

  const zielScore = useMemo(
    () => calculateOverallSovScore(state.zielSelections, state.sovWeights, sovData, subPointIds, state.zielOperationsAnswers, allSubPoints),
    [state.zielSelections, state.sovWeights, sovData, subPointIds, state.zielOperationsAnswers, allSubPoints],
  );

  const sovObjectives = sovData.metadata.sovObjectives;

  // 2. Step transition handler
  const handleStepChange = useCallback((step: WorkplaceSovStep) => {
    if (step === 'solution-builder' && state.currentStep === 'sov-weights') {
      state.initZielFromIst();
      state.initZielOperationsFromIst();
    }
    state.setCurrentStep(step);
  }, [state]);

  // 3. Ist-Zustand helpers — provider and deployment are set together
  const handleIstProvider = useCallback((fgId: string, provider: string | null, deployment?: DeploymentModel) => {
    state.setIstSelection(fgId, provider, deployment);
  }, [state]);

  const handleIstDeployment = useCallback((fgId: string, dep: DeploymentModel) => {
    const currentProvider = state.istSelections[fgId]?.provider;
    if (currentProvider) {
      state.setIstSelection(fgId, currentProvider, dep);
    }
  }, [state]);

  // Ist operations answer helper
  const handleIstOperationsAnswer = useCallback((qId: 'q1' | 'q2' | 'q3' | 'q4', sealValue: number) => {
    state.setOperationsAnswer('ist', qId, sealValue);
  }, [state]);

  // 4. Ziel helpers
  const handleZielProvider = useCallback((fgId: string, provider: string | null, deployment?: DeploymentModel) => {
    state.setZielSelection(fgId, provider, deployment);
  }, [state]);

  const handleZielDeployment = useCallback((fgId: string, dep: DeploymentModel) => {
    state.setZielSelection(fgId, state.zielSelections[fgId]?.provider ?? null, dep);
  }, [state]);

  // Ziel operations answer helper
  const handleZielOperationsAnswer = useCallback((qId: 'q1' | 'q2' | 'q3' | 'q4', sealValue: number) => {
    state.setOperationsAnswer('ziel', qId, sealValue);
  }, [state]);

  // 5. Email report
  const handleGenerateReport = useCallback(() => setEmailModalOpen(true), []);

  const handleEmailSubmit = useCallback(async (email: string, reportName: string) => {
    const pdfBase64 = generateWorkplacePdfBase64({
      levels,
      istSelections: state.istSelections,
      zielSelections: state.zielSelections,
      istScore,
      zielScore,
      sovWeights: state.sovWeights,
      sovObjectives,
      sealDefinitions,
      infrastructureMode: state.infrastructureMode,
      istOperationsAnswers: state.istOperationsAnswers,
      zielOperationsAnswers: state.zielOperationsAnswers,
      reportName,
    });
    const resp = await sendReport({
      email,
      pdfBase64,
      consent: true,
      consentTimestamp: new Date().toISOString(),
      language: language as 'de' | 'en',
      providerCount: aggregatedProviders.length,
      reportType: 'workplace',
    });
    if (!resp.success) throw new Error(resp.message || 'Report konnte nicht gesendet werden.');
  }, [levels, istScore, zielScore, state.istSelections, state.zielSelections, state.sovWeights, sovObjectives, sealDefinitions, state.infrastructureMode, state.istOperationsAnswers, state.zielOperationsAnswers, language, aggregatedProviders.length]);

  // 6. Sidebar
  const sidebarMenuItems: SidebarMenuItem[] = [
    { labelKey: 'sidebar.backToModeSelector', onClick: onBack },
    ...(onSwitchToCloud ? [{ labelKey: 'sidebar.switchToCloud', onClick: onSwitchToCloud }] : []),
    { labelKey: 'sidebar.regulatory', onClick: onOpenRegulatory },
    { labelKey: 'sidebar.reset', onClick: state.reset },
  ];

  const sidebarFooterLinks = [
    { label: 'Impressum', onClick: () => window.open('/impressum.html', '_blank') },
    ...(onOpenDatenschutz ? [{ label: 'Datenschutzerklärung', onClick: onOpenDatenschutz }] : []),
  ];

  // --- Error state ---
  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: theme.bg }}>
        <Header sidebarOpen={sidebarOpen} onToggleSidebar={() => setSidebarOpen(o => !o)} menuItems={sidebarMenuItems} sidebarFooterLinks={sidebarFooterLinks} />
        <div style={{ paddingTop: 80, textAlign: 'center', padding: '120px 16px' }}>
          <p style={{ color: theme.primary, fontSize: 18, fontWeight: 700 }}>{t('common.error' as any)}</p>
          <p style={{ color: theme.muted }}>{error}</p>
          <button onClick={onBack} style={{ marginTop: 16, border: `1px solid ${theme.border}`, background: theme.cardBg, padding: '8px 20px', borderRadius: 6, cursor: 'pointer' }}>
            {t('questionnaire.back' as any)}
          </button>
        </div>
      </div>
    );
  }

  // --- Saved state prompt ---
  if (state.hasSavedState && state.currentStep === 'infrastructure') {
    return (
      <div style={{ minHeight: '100vh', background: '#F4F4F4' }}>
        <Header sidebarOpen={sidebarOpen} onToggleSidebar={() => setSidebarOpen(o => !o)} menuItems={sidebarMenuItems} sidebarFooterLinks={sidebarFooterLinks} />
        <div style={{ height: 64 }} />
        <section style={{ maxWidth: 520, margin: '0 auto', padding: 16 }}>
          <div style={{ background: 'rgba(255,255,255,0.95)', border: `1px solid ${theme.border}`, borderRadius: 12, padding: 32, textAlign: 'center' }}>
            <h2 style={{ margin: '0 0 12px', color: theme.text, fontSize: 18 }}>{t('workplace.sovState.prompt' as any)}</h2>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={state.continueSavedState} style={{ padding: '10px 24px', borderRadius: 6, border: 'none', fontWeight: 700, fontSize: 15, background: theme.secondary, color: '#fff', cursor: 'pointer' }}>
                {t('workplace.sovState.continue' as any)}
              </button>
              <button onClick={state.reset} style={{ padding: '10px 24px', borderRadius: 6, border: `1px solid ${theme.border}`, fontWeight: 700, fontSize: 15, background: '#fff', color: theme.text, cursor: 'pointer' }}>
                {t('workplace.sovState.startNew' as any)}
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // --- Main 6-step layout ---
  return (
    <div style={{ minHeight: '100vh', background: '#F4F4F4', position: 'relative' }}>
      {/* Background image with white overlay and blur */}
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundImage: 'url(/arbeitsplatz.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        filter: 'blur(3px)',
        zIndex: 0,
        animation: 'wpBgFadeIn 2.5s ease-out forwards',
      }} />
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(255,255,255,0.55)',
        zIndex: 0,
        animation: 'wpOverlayFadeIn 2.5s ease-out forwards',
      }} />
      <style>{`
        @keyframes wpBgFadeIn { from { filter: blur(0px); } to { filter: blur(3px); } }
        @keyframes wpOverlayFadeIn { from { background: rgba(255,255,255,0); } to { background: rgba(255,255,255,0.55); } }
      `}</style>
      <div style={{ position: 'relative', zIndex: 1 }}>
      <Header sidebarOpen={sidebarOpen} onToggleSidebar={() => setSidebarOpen(o => !o)} menuItems={sidebarMenuItems} sidebarFooterLinks={sidebarFooterLinks} />
      <div style={{ height: 64 }} />
      <section style={{ maxWidth: 960, margin: '0 auto', padding: 16 }}>
        <WorkplaceProgressBar currentStep={state.currentStep} onStepClick={handleStepChange} />
        <div style={{ background: 'rgba(255,255,255,0.95)', border: `1px solid ${theme.border}`, borderRadius: 12, padding: 24, marginTop: 12, minHeight: 300 }}>
          {state.currentStep === 'infrastructure' && (
            <InfrastructureStep
              selectedMode={state.infrastructureMode}
              cloudProvider={state.cloudProvider}
              onSelectMode={state.setInfrastructureMode}
              onSelectCloudProvider={state.setCloudProvider}
              onContinue={() => handleStepChange('ist-zustand')}
            />
          )}
          {state.currentStep === 'ist-zustand' && (
            <IstZustandStep
              levels={levels}
              aggregatedProviders={aggregatedProviders}
              sovData={sovData}
              infrastructureMode={state.infrastructureMode ?? 'hybrid'}
              globalDeployment={globalDeployment}
              istSelections={state.istSelections}
              operationsAnswers={state.istOperationsAnswers}
              onOperationsAnswer={handleIstOperationsAnswer}
              onSelectProvider={handleIstProvider}
              onSelectDeployment={handleIstDeployment}
              onContinue={() => handleStepChange('ist-score')}
              onBack={() => handleStepChange('infrastructure')}
            />
          )}
          {state.currentStep === 'ist-score' && (
            <IstScoreStep
              istScore={istScore}
              istSelections={state.istSelections}
              sovWeights={state.sovWeights}
              levels={levels}
              aggregatedProviders={aggregatedProviders}
              sealDefinitions={sealDefinitions}
              sovObjectives={sovObjectives}
              onContinue={() => handleStepChange('sov-weights')}
              onBack={() => handleStepChange('ist-zustand')}
            />
          )}
          {state.currentStep === 'sov-weights' && (
            <SovWeightStep
              weights={state.sovWeights}
              onChangeWeight={state.setSovWeight}
              onResetWeights={state.resetWeights}
              istScore={istScore}
              onContinue={() => handleStepChange('solution-builder')}
              onBack={() => handleStepChange('ist-score')}
            />
          )}
          {state.currentStep === 'solution-builder' && (
            <SolutionBuilder
              levels={levels}
              aggregatedProviders={aggregatedProviders}
              sovData={sovData}
              sovWeights={state.sovWeights}
              istSelections={state.istSelections}
              zielSelections={state.zielSelections}
              istScore={istScore}
              zielScore={zielScore}
              operationsAnswers={state.zielOperationsAnswers}
              onOperationsAnswer={handleZielOperationsAnswer}
              onSelectProvider={handleZielProvider}
              onSelectDeployment={handleZielDeployment}
              onContinue={() => handleStepChange('results')}
              onBack={() => handleStepChange('sov-weights')}
            />
          )}
          {state.currentStep === 'results' && (
            <WorkplaceResults
              istScore={istScore}
              zielScore={zielScore}
              istSelections={state.istSelections}
              zielSelections={state.zielSelections}
              istOperationsAnswers={state.istOperationsAnswers}
              zielOperationsAnswers={state.zielOperationsAnswers}
              sovWeights={state.sovWeights}
              levels={levels}
              aggregatedProviders={aggregatedProviders}
              sealDefinitions={sealDefinitions}
              sovObjectives={sovObjectives}
              onGenerateReport={handleGenerateReport}
              onBack={() => handleStepChange('solution-builder')}
              onReset={state.reset}
            />
          )}
        </div>
      </section>
      <EmailModal isOpen={emailModalOpen} onClose={() => setEmailModalOpen(false)} onSubmit={handleEmailSubmit} />
      </div>
    </div>
  );
};
